import { useReimbursementRequestContext } from "../../../Contexts/ReimbursementRequestContext";
import SearchSelect from "../../../Components/SearchSelect";
import DatePicker from "../../../Components/DatePicker";
import InputField from "../../../Components/InputField";
import { useAuth } from "../../../Contexts/AuthContext";
import { FormControl } from "@mui/material";
import dayjs from "dayjs";

function RequestForm() {
  const {
    setIsGettingHospitals,
    setIsGettingDoctors,
    isGettingHospitals,
    isGettingDoctors,
    setErrorMessage,
    setErrorField,
    setOpenField,
    allHospitals,
    setHospitals,
    setFormData,
    allDoctors,
    setDoctors,
    errorField,
    openField,
    hospitals,
    formData,
    doctors,
  } = useReimbursementRequestContext();

  const { isPhysiciansLoad } = useAuth();

  const handleDateChange = (name, date) => {
    let newForm = {
      ...formData,
      [name]: date,
    };
    setFormData(newForm);
  };

  const handleCheckChange = (name, isNotInList) => {
    let fieldNames = {
      isHospitalNotInList: "hospital",
      isDoctorNotInList: "doctor",
    };
    let newForm = {
      ...formData,
      [name]: isNotInList,
      [fieldNames[name]]: null,
    };
    setFormData(newForm);
  };

  const formatNumber = (num) => {
    const cleaned = num.replace(/[^0-9.]/g, "");
    const [integerPart, decimalPart] = cleaned.split(".");
    const integerNumber = parseInt(integerPart, 10);
    if (isNaN(integerNumber)) return cleaned;
    const formattedIntegerPart = integerNumber.toLocaleString();
    return decimalPart !== undefined
      ? `${formattedIntegerPart}.${decimalPart}`
      : formattedIntegerPart;
  };

  const handleNumberChange = (e) => {
    let maxValue = 1000000000;
    let value = e.target.value;
    const cleaned = value.replace(/[^0-9.]/g, "");
    if (cleaned > maxValue) {
      value = maxValue.toLocaleString();
    }
    const formattedValue = formatNumber(value);
    let newForm = {
      ...formData,
      [e.target.name]: formattedValue,
    };
    setFormData(newForm);
  };

  const handleSelectChange = (name, value) => {
    let newForm = {
      ...formData,
      [name]: value,
    };
    setFormData(newForm);
    setErrorMessage("");
    setErrorField("");
  };

  const renderOption = (props, option) => {
    return (
      <div
        {...props}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <p className="font-bold">{option.providerName}</p>
        <p className="text-sm text-[#60656D]">{option.address}</p>
      </div>
    );
  };

  const filterHospitals = (options, inputValue) => {
    let arr = [];
    if (inputValue) {
      arr = options.filter((item) => {
        let name = item.providerName || "";
        return name.toLowerCase().includes(inputValue.toLowerCase());
      });
    }
    return arr;
  };

  const sortHospitals = (input) => {
    let sorted = [];
    if (input) {
      sorted = allHospitals.sort((a, b) => {
        let aStr = a.providerName || "";
        let bStr = b.providerName || "";

        if (
          aStr.toLowerCase().startsWith(input.toLowerCase()) &&
          bStr.toLowerCase().startsWith(input.toLowerCase())
        ) {
          return aStr.localeCompare(bStr);
        } else if (aStr.toLowerCase().startsWith(input.toLowerCase())) {
          return -1;
        } else if (bStr.toLowerCase().startsWith(input.toLowerCase())) {
          return 1;
        }
        return aStr.toLowerCase().localeCompare(bStr.toLowerCase());
      });
    }
    return sorted;
  };

  const handleHospitalInputChange = (e, v) => {
    setIsGettingHospitals(true);
    const sorted = sortHospitals(v);
    const filtered = filterHospitals(sorted, v);
    const chunkOptions = filtered.slice(0, 100);
    setHospitals(chunkOptions);
    setIsGettingHospitals(false);
  };

  const renderDoctorOption = (props, option) => {
    return <div {...props}>{option.Physician?.split(":")[0].trim() || ""}</div>;
  };

  const filterDoctors = (options, inputValue) => {
    let arr = [];
    if (inputValue) {
      arr = options.filter((item) => {
        let name = item.Physician?.split(":")[0].trim() || "";
        return name.toLowerCase().includes(inputValue.toLowerCase());
      });
    }
    return arr;
  };

  const sortDoctors = (input) => {
    let sorted = [];
    if (input) {
      sorted = allDoctors.sort((a, b) => {
        let aStr = a.Physician?.split(":")[0].trim() || "";
        let bStr = b.Physician?.split(":")[0].trim() || "";

        if (
          aStr.toLowerCase().startsWith(input.toLowerCase()) &&
          bStr.toLowerCase().startsWith(input.toLowerCase())
        ) {
          return aStr.localeCompare(bStr);
        } else if (aStr.toLowerCase().startsWith(input.toLowerCase())) {
          return -1;
        } else if (bStr.toLowerCase().startsWith(input.toLowerCase())) {
          return 1;
        }
        return aStr.toLowerCase().localeCompare(bStr.toLowerCase());
      });
    }
    return sorted;
  };

  const handleDoctorInputChange = (e, v) => {
    setIsGettingDoctors(true);
    const sorted = sortDoctors(v);
    const filtered = filterDoctors(sorted, v);
    const chunkOptions = filtered.slice(0, 100);
    setDoctors(chunkOptions);
    setIsGettingDoctors(false);
  };

  return (
    <div className="p-4">
      <p className="text-[#60656D] mb-4">
        Please fill out the form below to request reimbursement
      </p>
      <FormControl sx={{ width: "100%", mb: 2 }}>
        <p className="font-semibold">Total amount of claim</p>
        <div className="mt-1">
          <InputField
            type="text"
            name="claimAmount"
            value={formData.claimAmount}
            onChange={handleNumberChange}
          />
        </div>
      </FormControl>
      <FormControl sx={{ width: "100%", mb: 2 }}>
        <p className="font-semibold">Hospital/Clinic</p>
        <div className="mt-1">
          <SearchSelect
            options={hospitals}
            open={openField === "hospital"}
            value={formData.hospital}
            onChange={(v) => handleSelectChange("hospital", v)}
            onOpen={() => setOpenField("hospital")}
            onClose={() => setOpenField("")}
            renderOption={renderOption}
            onInputChange={handleHospitalInputChange}
            getOptionLabel={(o) => o.providerName}
            isOptionEqualToValue={(o, v) => o.providerCode === v.providerCode}
            noOptionsText="No hospital found"
            loading={isGettingHospitals}
            error={errorField === "hospital"}
            helperText={
              errorField === "hospital" &&
              "Oops! It seems like you forgot to enter the hospital name"
            }
          />
        </div>
        <div className="flex flex-row mt-2">
          <input
            id="isHospitalNotInList"
            className="w-3"
            type="checkbox"
            checked={formData.isHospitalNotInList}
            onChange={() =>
              handleCheckChange(
                "isHospitalNotInList",
                !formData.isHospitalNotInList
              )
            }
          />
          <label
            htmlFor="isHospitalNotInList"
            className="ml-1 text-sm text-[#A3A7AB]"
          >
            The hospital is not in the list
          </label>
        </div>
      </FormControl>
      <FormControl sx={{ width: "100%", mb: 2 }}>
        <p className="font-semibold">Doctor's Name</p>
        <div className="mt-1">
          <SearchSelect
            disabled={isPhysiciansLoad}
            options={doctors}
            open={openField === "doctor"}
            value={formData.doctor}
            onChange={(v) => handleSelectChange("doctor", v)}
            onOpen={() => setOpenField("doctor")}
            onClose={() => setOpenField("")}
            renderOption={renderDoctorOption}
            onInputChange={handleDoctorInputChange}
            getOptionLabel={(o) => o.Physician?.split(":")[0].trim() || ""}
            getOptionKey={(o) => o.Provider + o.Physician}
            isOptionEqualToValue={(o, v) => o.Physician === v.Physician}
            noOptionsText="No doctor found"
            loading={isGettingDoctors || isPhysiciansLoad}
            error={errorField === "doctor"}
            helperText={
              errorField === "doctor" &&
              "Oops! It seems like you forgot to enter the name of requesting doctor"
            }
          />
        </div>
        <div className="flex flex-row mt-2">
          <input
            id="isDoctorNotInList"
            className="w-3"
            type="checkbox"
            checked={formData.isDoctorNotInList}
            onChange={() =>
              handleCheckChange(
                "isDoctorNotInList",
                !formData.isDoctorNotInList
              )
            }
          />
          <label
            htmlFor="isDoctorNotInList"
            className="ml-1 text-sm text-[#A3A7AB]"
          >
            The doctor is not in the list
          </label>
        </div>
      </FormControl>
      <FormControl sx={{ width: "100%", mb: 2 }}>
        <p className="font-semibold">Availment / Admission Date</p>
        <div className="mt-1">
          <DatePicker
            name="admissionDate"
            value={
              formData.admissionDate ? dayjs(formData.admissionDate) : null
            }
            onAccept={(value) => handleDateChange("admissionDate", value)}
            maxDate={dayjs()}
          />
        </div>
      </FormControl>
      <FormControl sx={{ width: "100%" }}>
        <p className="font-semibold">Discharge Date</p>
        <div className="mt-1">
          <DatePicker
            disabled={formData.claimType !== "In Patient (IP)"}
            name="dischargeDate"
            value={
              formData.dischargeDate ? dayjs(formData.dischargeDate) : null
            }
            onAccept={(value) => handleDateChange("dischargeDate", value)}
            maxDate={dayjs()}
          />
        </div>
      </FormControl>
    </div>
  );
}

export default RequestForm;
